import React, { useEffect, useState, useMemo } from 'react'
import {
    Box,
    Typography,
    Button,
    TextField,
    CircularProgress,
    makeStyles,
    Theme,
    FormControlLabel,
    Radio,
    RadioGroup,
    Paper,
    Chip,
    IconButton,
    Tooltip,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import HomeIcon from '@material-ui/icons/Home'
import FilterListIcon from '@material-ui/icons/FilterList'
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck'
import EmailIcon from '@material-ui/icons/Email'
import SelectAllIcon from '@material-ui/icons/SelectAll'
import { useUser } from '../../../../hooks/useUser'
import { axiosInstance } from '../../../../helpers'
import { toast } from 'react-toastify'
import { APARTMENT } from '../../../../helpers'
import { Company } from '../../../../models/Company'
import { Selector } from '../../../../components/Selector'

export type CopyType = 'inspection_type' | 'move_out_rule'

export interface InspectionTypeItem {
    id: number
    name: string
    category: string
    area_order: string
    created_date: string
    modified_date: string
}

export interface MoveOutRuleStep {
    id: number
    inspection_type: number | null
    service: number | null
    rank: number
    created_date: string
    modified_date: string
    vendor_rules: unknown[]
    move_out_rule: number
}

export interface MoveOutRuleItem {
    id: number
    apartment: number
    allow_weekends: boolean
    name: string
    steps: MoveOutRuleStep[]
    created_date: string
    modified_date: string
    is_default: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        backgroundColor: theme.palette.grey[50],
        width: '100%',
    },
    header: {
        marginBottom: theme.spacing(4),
    },
    title: {
        fontSize: '28px',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        color: theme.palette.text.primary,
    },
    subtitle: {
        marginTop: theme.spacing(0.5),
        fontSize: '15px',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
    },
    stepCard: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(2),
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        },
    },
    stepHeader: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        gap: theme.spacing(1.5),
    },
    stepTitle: {
        fontSize: '16px',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    stepIcon: {
        color: theme.palette.primary.main,
        fontSize: 22,
    },
    stepDoneIcon: {
        color: theme.palette.success.main,
        fontSize: 22,
    },
    field: {
        marginTop: theme.spacing(1.5),
        '&:first-of-type': {
            marginTop: 0,
        },
    },
    radioGroup: {
        marginTop: theme.spacing(1),
    },
    radioLabel: {
        fontWeight: 500,
    },
    summaryChip: {
        marginTop: theme.spacing(1.5),
        fontWeight: 500,
    },
    targetChipsSummary: {
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(0.5),
        fontWeight: 500,
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
    },
    targetChipsWrap: {
        marginTop: theme.spacing(1),
        maxHeight: 220,
        overflowY: 'auto',
        padding: theme.spacing(1.5),
        borderRadius: 8,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
    },
    targetChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    submitCard: {
        padding: theme.spacing(3),
        marginTop: theme.spacing(1),
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
    submitRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    primaryButton: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: 8,
    },
    emailField: {
        marginTop: 0,
    },
    selectAllWrap: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1.5),
        gap: theme.spacing(1),
    },
    selectAllButton: {
        textTransform: 'none',
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: theme.palette.primary.main,
        padding: theme.spacing(0.5, 1.25),
        borderRadius: 8,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.primary.dark,
        },
        '& .MuiButton-startIcon': {
            marginRight: theme.spacing(0.5),
        },
    },
    clearAllButton: {
        padding: theme.spacing(0.5),
        color: theme.palette.text.secondary,
        '&:hover': {
            color: theme.palette.error.main,
            backgroundColor: theme.palette.action.hover,
        },
    },
}))

export const CopyToolPage: React.FC = () => {
    const classes = useStyles()
    const {
        organization,
        actions,
        organizationCompanyList,
        loading,
    } = useUser()

    const propertyList = useMemo(
        () =>
            organizationCompanyList.filter(
                (company) => company.company_type === APARTMENT,
            ),
        [organizationCompanyList],
    )

    useEffect(() => {
        if (organization) {
            actions.fetchOrganizationCompanyList(organization)
        }
    }, [organization])

    const [sourcePropertyId, setSourcePropertyId] = useState<number | ''>('')
    const [copyType, setCopyType] = useState<CopyType>('inspection_type')
    const [inspectionTypes, setInspectionTypes] = useState<
        InspectionTypeItem[]
    >([])
    const [moveOutRules, setMoveOutRules] = useState<MoveOutRuleItem[]>([])
    const [loadingItems, setLoadingItems] = useState(false)
    const [selectedInspectionTypeId, setSelectedInspectionTypeId] = useState<
        number | ''
    >('')
    const [selectedMoveOutRuleId, setSelectedMoveOutRuleId] = useState<
        number | ''
    >('')
    const [targetPropertyIds, setTargetPropertyIds] = useState<number[]>([])
    const [email, setEmail] = useState('')
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        setSourcePropertyId('')
        setCopyType('inspection_type')
        setInspectionTypes([])
        setMoveOutRules([])
        setLoadingItems(false)
        setSelectedInspectionTypeId('')
        setSelectedMoveOutRuleId('')
        setTargetPropertyIds([])
        setEmail('')
    }, [organization])

    useEffect(() => {
        if (!organization || !sourcePropertyId) {
            setInspectionTypes([])
            setMoveOutRules([])
            setSelectedInspectionTypeId('')
            setSelectedMoveOutRuleId('')
            return
        }
        // When org switches we reset sourcePropertyId; avoid fetching for the old
        // apartment while the new org's company list is still loading.
        if (loading.organizationCompanyList) {
            setInspectionTypes([])
            setMoveOutRules([])
            setSelectedInspectionTypeId('')
            setSelectedMoveOutRuleId('')
            return
        }
        const aptId = Number(sourcePropertyId)
        setLoadingItems(true)
        if (copyType === 'inspection_type') {
            axiosInstance
                .get(`organization/${organization}/inspection-types/`, {
                    params: { apartment_id: aptId },
                })
                .then((res) => {
                    setInspectionTypes(res.data || [])
                    setSelectedInspectionTypeId('')
                })
                .catch(() => {
                    toast.error('Failed to load inspection types')
                    setInspectionTypes([])
                })
                .finally(() => setLoadingItems(false))
        } else {
            axiosInstance
                .get(`organization/${organization}/move-out-rules/`, {
                    params: { apartment_id: aptId, archived: false },
                })
                .then((res) => {
                    setMoveOutRules(res.data || [])
                    setSelectedMoveOutRuleId('')
                })
                .catch(() => {
                    toast.error('Failed to load move-out rules')
                    setMoveOutRules([])
                })
                .finally(() => setLoadingItems(false))
        }
    }, [
        organization,
        sourcePropertyId,
        copyType,
        loading.organizationCompanyList,
    ])

    const handleCopy = () => {
        if (!organization || !sourcePropertyId) {
            toast.error('Please select a source property')
            return
        }
        if (targetPropertyIds.length === 0) {
            toast.error('Please select at least one target property')
            return
        }
        const inspectionTypeName =
            copyType === 'inspection_type'
                ? inspectionTypes.find(
                      (it) => it.id === selectedInspectionTypeId,
                  )?.name
                : null
        if (copyType === 'inspection_type' && !inspectionTypeName) {
            toast.error('Please select an inspection type to copy')
            return
        }
        if (copyType === 'move_out_rule' && !selectedMoveOutRuleId) {
            toast.error('Please select a move-out rule to copy')
            return
        }
        if (!email.trim()) {
            toast.error('Please enter an email address')
            return
        }

        const body: Record<string, unknown> = {
            copy_type: copyType,
            source_apt: Number(sourcePropertyId),
            targets: targetPropertyIds,
            email: email.trim(),
        }
        if (copyType === 'inspection_type') {
            body.inspection_type_name = inspectionTypeName
        } else {
            body.move_out_rule_id = Number(selectedMoveOutRuleId)
        }

        setSubmitting(true)
        axiosInstance
            .post(`organization/${organization}/copy-config/`, body)
            .then((res) => {
                if (res.status === 202) {
                    toast.success(
                        'Copy started. You will receive an email when it completes.',
                    )
                    setTargetPropertyIds([])
                    setEmail('')
                }
            })
            .catch((err) => {
                const detail =
                    err.response?.data?.detail || 'Failed to start copy'
                toast.error(detail)
            })
            .finally(() => setSubmitting(false))
    }

    const targetOptions = propertyList.filter((p) => p.id !== sourcePropertyId)

    const sourceProperty = propertyList.find((p) => p.id === sourcePropertyId)
    const step1Done = !!sourcePropertyId
    const hasItemSelected =
        copyType === 'inspection_type'
            ? !!selectedInspectionTypeId
            : !!selectedMoveOutRuleId
    const step2Done = step1Done && !loadingItems && hasItemSelected
    const step3Done = targetPropertyIds.length > 0

    const selectedItemLabel =
        copyType === 'inspection_type'
            ? inspectionTypes.find((it) => it.id === selectedInspectionTypeId)
                  ?.name
            : moveOutRules.find((r) => r.id === selectedMoveOutRuleId)?.name

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography className={classes.title}>Copy Tool</Typography>
                <Typography className={classes.subtitle}>
                    Copy inspection types or move-out rules from one property to
                    others. Complete each step to continue.
                </Typography>
            </Box>

            {loading.organizationCompanyList ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={8}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Step 1: Source property — always visible */}
                    <Paper className={classes.stepCard} elevation={0}>
                        <div className={classes.stepHeader}>
                            {step1Done ? (
                                <CheckCircleIcon
                                    className={classes.stepDoneIcon}
                                />
                            ) : (
                                <HomeIcon className={classes.stepIcon} />
                            )}
                            <Typography className={classes.stepTitle}>
                                {step1Done
                                    ? 'Source property'
                                    : '1. Choose source property'}
                            </Typography>
                        </div>
                        {!step1Done ? (
                            <Selector<Company>
                                label="Copy from"
                                data={propertyList}
                                getDisplayString={(p) => p.name}
                                currentValue={
                                    sourcePropertyId === ''
                                        ? -1
                                        : sourcePropertyId
                                }
                                onChange={(e) => {
                                    const v = e.target.value as number
                                    setSourcePropertyId(v === -1 ? '' : v)
                                }}
                                noSelectionLabel="Select property"
                                searchable
                                size="small"
                                customStyle={{
                                    formControl: { width: '100%', minWidth: 0 },
                                }}
                            />
                        ) : (
                            <Chip
                                label={sourceProperty?.name}
                                className={classes.summaryChip}
                                onDelete={() => {
                                    setSourcePropertyId('')
                                    setSelectedInspectionTypeId('')
                                    setSelectedMoveOutRuleId('')
                                    setTargetPropertyIds([])
                                }}
                            />
                        )}
                    </Paper>

                    {/* Step 2: What to copy + which item — only after step 1 */}
                    {step1Done && (
                        <Paper className={classes.stepCard} elevation={0}>
                            <div className={classes.stepHeader}>
                                {step2Done ? (
                                    <CheckCircleIcon
                                        className={classes.stepDoneIcon}
                                    />
                                ) : (
                                    <FilterListIcon
                                        className={classes.stepIcon}
                                    />
                                )}
                                <Typography className={classes.stepTitle}>
                                    {step2Done
                                        ? 'What to copy'
                                        : '2. Choose what to copy'}
                                </Typography>
                            </div>
                            {!step2Done ? (
                                <>
                                    <RadioGroup
                                        row
                                        value={copyType}
                                        onChange={(e) =>
                                            setCopyType(
                                                e.target.value as CopyType,
                                            )
                                        }
                                        className={classes.radioGroup}
                                    >
                                        <FormControlLabel
                                            value="inspection_type"
                                            control={<Radio color="primary" />}
                                            label="Inspection Type"
                                            className={classes.radioLabel}
                                        />
                                        <FormControlLabel
                                            value="move_out_rule"
                                            control={<Radio color="primary" />}
                                            label="Move Out Rule"
                                            className={classes.radioLabel}
                                        />
                                    </RadioGroup>

                                    {loadingItems ? (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            py={2}
                                            style={{ gap: 8 }}
                                        >
                                            <CircularProgress size={20} />
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                Loading…
                                            </Typography>
                                        </Box>
                                    ) : copyType === 'inspection_type' ? (
                                        <Selector<InspectionTypeItem>
                                            label="Inspection type"
                                            data={inspectionTypes}
                                            getDisplayString={(it) => it.name}
                                            currentValue={
                                                selectedInspectionTypeId === ''
                                                    ? -1
                                                    : selectedInspectionTypeId
                                            }
                                            onChange={(e) => {
                                                const v = e.target
                                                    .value as number
                                                setSelectedInspectionTypeId(
                                                    v === -1 ? '' : v,
                                                )
                                            }}
                                            noSelectionLabel="Select type"
                                            searchable
                                            size="small"
                                            customStyle={{
                                                formControl: {
                                                    width: '100%',
                                                    minWidth: 0,
                                                },
                                            }}
                                        />
                                    ) : (
                                        <Selector<MoveOutRuleItem>
                                            label="Move-out rule"
                                            data={moveOutRules}
                                            getDisplayString={(r) =>
                                                `${r.name}${
                                                    r.is_default
                                                        ? ' (default)'
                                                        : ''
                                                }`
                                            }
                                            currentValue={
                                                selectedMoveOutRuleId === ''
                                                    ? -1
                                                    : selectedMoveOutRuleId
                                            }
                                            onChange={(e) => {
                                                const v = e.target
                                                    .value as number
                                                setSelectedMoveOutRuleId(
                                                    v === -1 ? '' : v,
                                                )
                                            }}
                                            noSelectionLabel="Select rule"
                                            searchable
                                            size="small"
                                            customStyle={{
                                                formControl: {
                                                    width: '100%',
                                                    minWidth: 0,
                                                },
                                            }}
                                        />
                                    )}
                                </>
                            ) : (
                                <Chip
                                    label={selectedItemLabel}
                                    className={classes.summaryChip}
                                    onDelete={() => {
                                        setSelectedInspectionTypeId('')
                                        setSelectedMoveOutRuleId('')
                                        setTargetPropertyIds([])
                                    }}
                                />
                            )}
                        </Paper>
                    )}

                    {/* Step 3: Target properties — only after step 2 */}
                    {step2Done && (
                        <Paper className={classes.stepCard} elevation={0}>
                            <div className={classes.stepHeader}>
                                {step3Done ? (
                                    <CheckCircleIcon
                                        className={classes.stepDoneIcon}
                                    />
                                ) : (
                                    <PlaylistAddCheckIcon
                                        className={classes.stepIcon}
                                    />
                                )}
                                <Typography className={classes.stepTitle}>
                                    {step3Done
                                        ? 'Target properties'
                                        : '3. Choose properties to copy to'}
                                </Typography>
                            </div>
                            <Box className={classes.selectAllWrap}>
                                <Button
                                    size="small"
                                    startIcon={
                                        <SelectAllIcon fontSize="small" />
                                    }
                                    className={classes.selectAllButton}
                                    onClick={() => {
                                        setTargetPropertyIds(
                                            targetOptions.map((p) => p.id),
                                        )
                                    }}
                                >
                                    Select all {targetOptions.length} properties
                                </Button>
                                {targetPropertyIds.length > 0 && (
                                    <Tooltip title="Clear all selected">
                                        <IconButton
                                            size="small"
                                            className={classes.clearAllButton}
                                            onClick={() =>
                                                setTargetPropertyIds([])
                                            }
                                            aria-label="Clear all selected"
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <Selector<Company>
                                label="Target properties"
                                data={targetOptions}
                                getDisplayString={(p) => p.name}
                                currentValue={targetPropertyIds}
                                onChange={(e) => {
                                    const v = e.target.value
                                    setTargetPropertyIds(
                                        Array.isArray(v)
                                            ? v
                                            : [v].filter(Boolean),
                                    )
                                }}
                                multiple
                                searchable
                                size="small"
                                popupHeight={320}
                                customStyle={{
                                    formControl: {
                                        width: '100%',
                                        minWidth: 0,
                                    },
                                }}
                                customRenderValue={(selected) => {
                                    const arr = (selected as number[]) || []
                                    if (arr.length === 0) {
                                        return (
                                            <span
                                                style={{
                                                    color: 'rgba(0,0,0,0.54)',
                                                }}
                                            >
                                                Select properties
                                            </span>
                                        )
                                    }
                                    return (
                                        <span>
                                            {arr.length}{' '}
                                            {arr.length === 1
                                                ? 'property'
                                                : 'properties'}{' '}
                                            selected
                                        </span>
                                    )
                                }}
                            />
                            {targetPropertyIds.length > 0 && (
                                <>
                                    <Typography
                                        className={classes.targetChipsSummary}
                                        variant="body2"
                                    >
                                        Selected — remove any below or add more
                                        from the dropdown above
                                    </Typography>
                                    <Box className={classes.targetChipsWrap}>
                                        <Box className={classes.targetChips}>
                                            {targetPropertyIds.map((id) => {
                                                const p = propertyList.find(
                                                    (x) => x.id === id,
                                                )
                                                return (
                                                    <Chip
                                                        key={id}
                                                        label={p?.name ?? id}
                                                        size="small"
                                                        onDelete={() =>
                                                            setTargetPropertyIds(
                                                                (prev) =>
                                                                    prev.filter(
                                                                        (x) =>
                                                                            x !==
                                                                            id,
                                                                    ),
                                                            )
                                                        }
                                                    />
                                                )
                                            })}
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </Paper>
                    )}

                    {/* Step 4: Email + Submit — only after step 3 */}
                    {step3Done && (
                        <Paper className={classes.submitCard} elevation={0}>
                            <div className={classes.stepHeader}>
                                <EmailIcon className={classes.stepIcon} />
                                <Typography className={classes.stepTitle}>
                                    4. Email to notify
                                </Typography>
                            </div>
                            <TextField
                                className={classes.emailField}
                                variant="outlined"
                                size="small"
                                fullWidth
                                required
                                type="email"
                                label="Email when complete"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <div className={classes.submitRow}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    className={classes.primaryButton}
                                    onClick={handleCopy}
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <CircularProgress
                                                size={20}
                                                style={{ marginRight: 8 }}
                                            />
                                            Starting…
                                        </>
                                    ) : (
                                        'Start copy'
                                    )}
                                </Button>
                            </div>
                        </Paper>
                    )}
                </>
            )}
        </Box>
    )
}
